import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface FieldConfig {
  readonly id: string;
  readonly label: string;
  readonly alwaysOn: boolean;
  readonly enabled: boolean;
}

interface ContactFormStepProps {
  readonly onComplete: (fields: readonly { id: string; enabled: boolean }[]) => void;
}

const DEFAULT_FIELDS: readonly FieldConfig[] = [
  { id: "name", label: "\u05E9\u05DD", alwaysOn: true, enabled: true },
  { id: "phone", label: "\u05D8\u05DC\u05E4\u05D5\u05DF", alwaysOn: true, enabled: true },
  { id: "email", label: '\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC', alwaysOn: false, enabled: true },
  { id: "message", label: "\u05D4\u05D5\u05D3\u05E2\u05D4", alwaysOn: false, enabled: true },
];

export function ContactFormStep({ onComplete }: ContactFormStepProps): ReactNode {
  const [fields, setFields] = useState<readonly FieldConfig[]>(DEFAULT_FIELDS);
  const [waNotify, setWaNotify] = useState(true);

  const toggleField = useCallback((fieldId: string) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId && !f.alwaysOn ? { ...f, enabled: !f.enabled } : f,
      ),
    );
  }, []);

  const handleSubmit = useCallback(() => {
    onComplete(
      fields.map((f) => ({ id: f.id, enabled: f.enabled })),
    );
  }, [fields, onComplete]);

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D8\u05D5\u05E4\u05E1 \u05DC\u05D9\u05D3\u05D9\u05DD"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D4\u05D2\u05D3\u05D9\u05E8\u05D5 \u05D0\u05D9\u05DC\u05D5 \u05E9\u05D3\u05D5\u05EA \u05D9\u05D5\u05E4\u05D9\u05E2\u05D5 \u05D1\u05D8\u05D5\u05E4\u05E1 \u05D4\u05DC\u05D9\u05D3\u05D9\u05DD \u05D1\u05D0\u05EA\u05E8."}
      </p>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          {fields.map((field) => (
            <button
              key={field.id}
              type="button"
              className="flex min-h-[44px] items-center gap-3 text-start"
              onClick={() => toggleField(field.id)}
              disabled={field.alwaysOn}
            >
              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                field.enabled ? "border-primary bg-primary" : "border-muted-foreground/40"
              } ${field.alwaysOn ? "opacity-60" : ""}`}>
                {field.enabled && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </div>
              <span className="font-medium">{field.label}</span>
              {field.alwaysOn && (
                <span className="text-xs text-muted-foreground">
                  {"\u0028\u05D7\u05D5\u05D1\u05D4\u0029"}
                </span>
              )}
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <h3 className="text-sm font-semibold">{"\u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4:"}</h3>
          <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
            {fields.filter((f) => f.enabled).map((field) => (
              <div key={field.id} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{field.label}</span>
                <div className="h-8 rounded border bg-background" />
              </div>
            ))}
            <div className="mt-1 h-9 rounded bg-primary/20" />
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp notification toggle */}
      <Card>
        <CardContent className="p-4">
          <button
            type="button"
            className="flex min-h-[44px] w-full items-center gap-3 text-start"
            onClick={() => setWaNotify((v) => !v)}
          >
            <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
              waNotify ? "border-primary bg-primary" : "border-muted-foreground/40"
            }`}>
              {waNotify && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
            </div>
            <span className="font-medium">{"\u05D4\u05EA\u05E8\u05D0\u05D4 \u05D1\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4 \u05E2\u05DC \u05DC\u05D9\u05D3 \u05D7\u05D3\u05E9"}</span>
          </button>
        </CardContent>
      </Card>

      <Button size="lg" className="min-h-[44px]" onClick={handleSubmit}>
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </div>
  );
}
